/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { PriceComponent } from '@msdyn365-commerce/components';
import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { CartLine, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
import { Quantity } from '../../utilities/quantity';
import {
    AttributesForSelectedVariantInput,
    getAttributesForSelectedVariantAction
} from '@msdyn365-commerce-modules/retail-actions';



/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
    /** Display string for discount label */
    discountStringText: string;
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** Display string for quantity label  */
    quantityDisplayString: string;
    /** Display string for quantity label  */
    inputQuantityAriaLabel: string;
    /** Aria label for the decrement button in quanitity component  */
    decrementButtonAriaLabel: string;
    /** Aria label for the increment button in quanitity component  */
    incrementButtonAriaLabel: string;
    salesLineSkuText: string;
}

interface ICartLineData {
    cartLine: CartLine | SalesLine;
    product: SimpleProduct;
    imperilDimension: string;
}

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static  */
    disableQuantityInteractivity?: boolean;
    /** The primary image url  */
    primaryImageUrl?: string;
    /** The product url  */
    productUrl?: string;
    /** GridSettings for the product image in cartLine  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartLine  */
    imageSettings: IImageSettings;
    /** Boolean flag to indicate if the item is out of stock  */
    isOutOfStock?: boolean;
    /** Flag to make quantity section editable  */
    isQuantityEditable?: boolean;
    /** Max quantity for line item  */
    maxQuantity?: number;
    /** Current quantity for line item  */
    currentQuantity?: number;
    /** Resource string for the component  */
    resources: ICartlineResourceString;
    /** SalesLine flag  */
    isSalesLine?: boolean;
    /** Error message to show in place of quantity */
    errorMessage?: string;
    /** Quantity onChange callback  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): void;

}

interface IDimensionStrings {
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
}

const CartLineItemFunctions = {
    renderProductDimensions: (Product: SimpleProduct, DimensionStrings: IDimensionStrings, props: any) => {
        if (!Product || !Product.Dimensions) {
            return [];
        }
        return Product.Dimensions.map(productDimension => {

            if (productDimension.DimensionTypeValue === 1) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>{DimensionStrings.colorString}
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span></span>
                    </div>
                );
            }
            if (productDimension.DimensionTypeValue === 2) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-configuration'>{DimensionStrings.configString}:
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span></span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 3) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>{DimensionStrings.sizeString}
                            {/* <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span> */}
                            <DimensionLine product={Product} context={props.context} defaultValue={productDimension.DimensionValue ? productDimension.DimensionValue.Value : ''} />
                        </span>

                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 4) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>{DimensionStrings.styleString}:
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span></span>
                    </div>
                );
            }
            return null;
        });
    },

    renderProductSKU:(props:ICartLineProps) => {
        var CartLine = props.data.cartLine;
        if (!CartLine || !CartLine.ItemId) {
            return undefined;
        }
        if(CartLine) {
            return(
                <div className='msc-cartline__product-item-id'>
                    <span className='msc-cartline__product-item-id__title'>SKU:</span>
                    <span className='msc-cartline__product-value'>{CartLine.ItemId}</span>
                </div>
            );
        }
        return undefined;
    },

    renderDiscountLines: (props: ICartLineProps) => {

        if (!props.data.cartLine.DiscountLines || props.data.cartLine.DiscountLines.length === 0) {
            return null;
        }
        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId || index} className='msc-cart-line-item-product-discount'>
                    <span>
                        {discountLine.OfferName ? discountLine.OfferName : ''}:
                        {` ${props.resources.discountStringText || 'Discount'} `}
                    </span>
                    <span className={'cart-line-item-product-discount-price'}>
                        <PriceComponent
                            data={props.isSalesLine ? {
                                price: {
                                    // @ts-ignore
                                    CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                }
                            } : {
                                price: {
                                    CustomerContextualPrice: props.data.cartLine.DiscountAmountWithoutTax,
                                    BasePrice: props.data.cartLine.DiscountAmount
                                }
                            }}
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                        />
                    </span>
                    <span>
                        {` (${discountLine.Percentage !== undefined ? discountLine.Percentage : ''}%)`}
                    </span>
                </div>
            );
        });
    }
};

class DimensionLine extends React.Component<any, { dimension: string }> {
    public dimension: string = '';
    constructor(props: any) {
        super(props);
        this.state = {
            // dimension:  props.data.product.result.Dimensions[1].DimensionValue.Value
            dimension: props.defaultValue
        };
    }
    public async componentDidMount(): Promise<void> {
        const actionInput = new AttributesForSelectedVariantInput(
            this.props.product.RecordId,
            // @ts-ignore
            +this.props.context.actionContext.requestContext.apiSettings.channelId,
            this.props.product);
        await getAttributesForSelectedVariantAction(actionInput, this.props.context.actionContext).then((result) => {
            result!.forEach(res => {
                if (res.Name === "Product dimensions imperial" && res.TextValue) {
                    this.setState({ dimension: res.TextValue })
                }
            })
        }).catch(error => {
            console.log('error', error);
        });
    }

    public render(): JSX.Element {
        return (
            <span className='name'> {this.state.dimension}</span>
        );
    }
}


// tslint:disable-next-line:max-func-body-length
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { isSalesLine, productUrl, resources } = props;
    const { product } = props.data;


    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString
    };
    const fallbackImageUrl = getFallbackImageUrl(product.ItemId, props.context.actionContext.requestContext.apiSettings);
    const productDimensions = CartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings, props);
    const productSKU = CartLineItemFunctions.renderProductSKU(props);
    const renderDisountLines = CartLineItemFunctions.renderDiscountLines(props);
    const onChange = (newValue: number) => {
        if (props.quantityOnChange) {
            props.quantityOnChange(props.data.cartLine, newValue);
        }
    };

    const _generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    const _generateQuantityandPrice = (): JSX.Element[] | null => {
        const nodes = [];
        // if (props.isOutOfStock) {
        //     const errorMessage = _generateErrorMessage();

        //     if (errorMessage) {
        //         return [errorMessage];
        //     }

        //     return null;
        // }
        if (props.isQuantityEditable && !props.data.cartLine.IsGiftCardLine) {
            const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
            const eoqQuantity = isB2B ? props.data?.product?.Behavior?.MultipleOfQuantity : 1;
            nodes.push(
                <div className='msc-cart-line__product-quantity'>
                    <div className='msc-cart-line__product-quantity-label'>{resources.quantityDisplayString}</div>

                    {_generateErrorMessage()}

                    <Quantity
                        id={`msc-cart-line__quantity_${props.data.product.RecordId}`}
                        max={props.maxQuantity || 10}
                        currentCount={props.currentQuantity}
                        onChange={onChange}
                        min={eoqQuantity}
                        inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                        decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                        key={props.data.cartLine.LineId}
                    />
                </div>
            );
        } else {
            nodes.push(
                <div className='msc-cart-line__quantity'>
                    <label className='quantity-label'>{resources.quantityDisplayString}:</label>

                    {_generateErrorMessage()}

                    <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                </div>
            );
        }
        const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';

        nodes.push(
            <div className='msc-cart-line__product-savings'>
                <PriceComponent
                    data={isSalesLine ? {
                        price: {
                            // @ts-ignore
                            CustomerContextualPrice: props.data.cartLine.Price - (props.data.cartLine.DiscountAmount / props.data.cartLine.Quantity!),
                            BasePrice: props.data.cartLine.NetPrice
                        }
                    } : {
                        price: {
                            // CustomerContextualPrice: props.data.cartLine.TaxAmount! + props.data.cartLine.NetAmountWithoutTax!,
                            // remove the GST from the product price
                            CustomerContextualPrice: isB2B? props.data.cartLine.NetAmountWithoutTax! : (props.data.cartLine.TaxAmount! + props.data.cartLine.NetAmountWithoutTax!),
                            BasePrice: props.data.cartLine.NetPrice
                        }
                    }}
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    className='msc-cart-line__product-savings-actual'
                />
                {
                    props.data.cartLine.DiscountAmount && props.data.cartLine.DiscountAmount > 0 &&
                    <>
                        <span className='msc-cart-line__product-savings-label'>{` ${props.resources.discountStringText || 'Discount'} `}</span>
                        <PriceComponent
                            data={props.isSalesLine ? {
                                price: {
                                    // @ts-ignore
                                    CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                }
                            } : {
                                price: {
                                    CustomerContextualPrice: props.data.cartLine.DiscountAmountWithoutTax,
                                    BasePrice: props.data.cartLine.DiscountAmount
                                }
                            }}
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            className='msc-cart-line__product-savings-text'
                        />
                    </> ||
                    null
                }
            </div>
        );

        return nodes;
    };
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    return (
        <div className='msc-cart-line'>
            <div className='msc-cart-line__product-image'>
                <Image src={props.primaryImageUrl || ''} fallBackSrc={fallbackImageUrl} altText={product.Name} gridSettings={props.gridSettings} imageSettings={props.imageSettings} loadFailureBehavior='empty' />
            </div>
            <div className='msc-cart-line__content'>
                <div className='msc-cart-line__product'>
                    <a className='msc-cart-line__product-title' href={productUrl}>{product.Name}</a>
                    {productDimensions.length > 0 ?
                        <div className='msc-cart-line__product-variants'>
                            {productDimensions}
                            {productSKU}
                            </div> :
                        ''
                    }
                    <div className='msc-cart-line__product-price'>
                        <PriceComponent
                            data={isSalesLine ? {
                                price: {
                                    // @ts-ignore
                                    //CustomerContextualPrice: props.data.cartLine.TotalAmount,
                                    CustomerContextualPrice: (isB2B && props.data.cartLine.DiscountAmount===0) ? (props.data.cartLine.TotalAmount/props.data.cartLine.Quantity!) : (props.data.cartLine.Price - (props.data.cartLine.DiscountAmount / props.data.cartLine.Quantity!)),
                                    BasePrice: props.data.cartLine.Price
                                }
                            } : {
                                price: {
                                    CustomerContextualPrice: props.data.cartLine.TaxAmount! + props.data.cartLine.NetAmountWithoutTax!,
                                    BasePrice: props.data.cartLine.Price
                                }
                            }}
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            className='discount-value'
                        />
                    </div>
                    {renderDisountLines}
                </div>
                {
                    _generateQuantityandPrice()
                }
                {props.data.cartLine.ItemId &&
                    <p className='sales-lines__item-detail sales-lines__item-sku'>
                        {resources.salesLineSkuText || 'SKU:'} {props.data.cartLine.ItemId}
                    </p>
                }
            </div>
        </div>
    );
};

export default CartLine;